import React, { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { useOrganisation } from '../../contexts/OrganisationContext';
import {
  BuildingOfficeIcon,
  ChevronDownIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import Logger from '../../utils/logger';
import { useTranslation } from 'react-i18next';

const OrganisationSelector: React.FC = () => {
  // @ts-ignore -- TS2589: useTranslation type instantiation too deep due to complex HeadlessUI generics in this component
  // (useTranslation as any) : neutralise TS2589 (react-i18next 14) à la source
  const { t } = (useTranslation as any)('organisations');
  const navigate = useNavigate();
  const {
    currentOrganisation,
    userOrganisations,
    currentUserRole,
    selectOrganisation,
    organisationsLoading,
  } = useOrganisation();

  const handleSelectOrganisation = async (orgId: string) => {
    try {
      await selectOrganisation(orgId);
      navigate(`/organisations/${orgId}/dashboard`);
    } catch (error) {
      Logger.error('Error selecting organisation:', error);
    }
  };

  const getRoleBadgeColor = (role: string | null) => {
    switch (role) {
      case 'owner':
        return 'bg-yellow-500/15 text-yellow-400';
      case 'admin':
        return 'bg-blue-500/15 text-blue-400';
      default:
        return 'bg-surface-4 text-text-tertiary';
    }
  };

  if (organisationsLoading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent-500 border-t-transparent"></div>
        <span className="text-sm text-text-tertiary">{t('selector.loading')}</span>
      </div>
    );
  }

  if (!currentOrganisation) {
    return (
      <button
        onClick={() => navigate('/organisations')}
        className="inline-flex items-center px-3 py-2 border border-border-1 rounded-button text-sm font-medium text-text-secondary bg-surface-3 hover:bg-surface-4 transition-colors"
      >
        <PlusIcon className="h-4 w-4 mr-2" />
        {t('selector.createOrg')}
      </button>
    );
  }

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="inline-flex items-center justify-between w-full px-3 py-2 border border-border-1 rounded-button text-sm font-medium text-text-secondary bg-surface-3 hover:bg-surface-4 focus:outline-none focus:ring-2 focus:ring-accent-500/40 focus:ring-offset-0 min-w-[200px] transition-colors">
          <div className="flex items-center space-x-2 flex-1 truncate">
            <BuildingOfficeIcon className="h-5 w-5 text-text-quaternary flex-shrink-0" />
            <span className="truncate">{currentOrganisation.nom}</span>
            {currentUserRole && (
              <span className={`text-xs px-2 py-0.5 rounded-badge flex-shrink-0 font-semibold ${getRoleBadgeColor(currentUserRole)}`}>
                {currentUserRole.toUpperCase()}
              </span>
            )}
          </div>
          <ChevronDownIcon className="h-5 w-5 text-text-quaternary ml-2 flex-shrink-0" />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute left-0 z-10 mt-2 w-80 origin-top-left rounded-card bg-surface-2 border border-border-1 shadow-card-hover focus:outline-none overflow-hidden">
          <div className="py-1">
            {/* Current Organization Header */}
            <div className="px-4 py-2 border-b border-border-0">
              <p className="text-xs text-text-quaternary uppercase font-semibold">
                {t('selector.currentOrg')}
              </p>
            </div>

            {/* Organisation courante. Le selecteur ne sert qu'a la voir et a en
                changer : les pages de l'organisation sont dans la barre laterale. */}
            <div className="px-4 py-3 bg-accent-500/8 border-b border-border-0">
              <div className="flex items-center space-x-3 min-w-0">
                <div className="h-9 w-9 rounded-button bg-accent-500/15 flex items-center justify-center flex-shrink-0">
                  <BuildingOfficeIcon className="h-5 w-5 text-accent-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-text-primary truncate">
                    {currentOrganisation.nom}
                  </p>
                  <p className="text-xs text-text-tertiary">
                    {t('selector.plan', { plan: currentOrganisation.plan_type })}
                  </p>
                </div>
              </div>
            </div>

            {/* Other Organizations */}
            {userOrganisations.length > 1 && (
              <>
                <div className="px-4 py-2 border-b border-border-0">
                  <p className="text-xs text-text-quaternary uppercase font-semibold">
                    {t('selector.switchOrg')}
                  </p>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {userOrganisations
                    .filter((org) => org.id !== currentOrganisation.id)
                    .map((org) => (
                      <Menu.Item key={org.id}>
                        {({ active }) => (
                          <button
                            onClick={() => handleSelectOrganisation(org.id)}
                            className={`${
                              active ? 'bg-surface-3' : ''
                            } group flex items-center justify-between w-full px-4 py-3 text-sm text-text-secondary transition-colors`}
                          >
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <BuildingOfficeIcon className="h-5 w-5 text-text-quaternary flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{org.nom}</p>
                                <p className="text-xs text-text-tertiary">
                                  {org.plan_type}
                                </p>
                              </div>
                            </div>
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                </div>
              </>
            )}

            {/* Actions */}
            <div className="border-t border-border-0">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => navigate('/organisations')}
                    className={`${
                      active ? 'bg-surface-3' : ''
                    } group flex items-center w-full px-4 py-3 text-sm text-text-secondary transition-colors`}
                  >
                    <BuildingOfficeIcon className="h-5 w-5 mr-3 text-text-quaternary" />
                    {t('selector.viewAll')}
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => navigate('/organisations/create')}
                    className={`${
                      active ? 'bg-surface-3' : ''
                    } group flex items-center w-full px-4 py-3 text-sm text-accent-400 font-medium transition-colors`}
                  >
                    <PlusIcon className="h-5 w-5 mr-3" />
                    {t('selector.createNew')}
                  </button>
                )}
              </Menu.Item>
            </div>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default OrganisationSelector;
